/**
 * The time-window dropdown, and the dates each choice means.
 *
 * The backend deliberately takes only startDate / endDate — presets are a layout
 * decision, so adding or renaming one is a frontend-only change (see the
 * GetIndustryPipelineFlow contract's "THE CALLER SUPPLIES DATES, NOT A PRESET").
 * That makes this file the only place the preset semantics live, which is why it
 * is a pure function with its own test rather than logic inside the Select.
 *
 * Anchoring, agreed with the PM. With today = 2026-09-15:
 *   上个月     2026-08-01 → 2026-08-31   (a complete month)
 *   本月       2026-09-01 → 2026-09-15   (partial, ends today)
 *   近三月     2026-07-01 → 2026-09-15   (first of the month two back → today)
 *   Q1        2026-01-01 → 2026-03-31   (complete quarter)
 *   Q3        2026-07-01 → 2026-09-15   (in progress, so capped at today)
 *   YTD       2026-01-01 → 2026-09-15
 *
 * A quarter still in progress ends today, not on its calendar end — asking the
 * server for a window that runs into the future would report zeros for days that
 * have not happened.
 */

export type WindowPresetId =
  | 'LAST_MONTH'
  | 'THIS_MONTH'
  | 'LAST_3_MONTHS'
  | 'Q1'
  | 'Q2'
  | 'Q3'
  | 'Q4'
  | 'YTD';

export interface WindowRange {
  /** Inclusive, `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive, `YYYY-MM-DD`. */
  endDate: string;
}

export interface WindowPreset extends WindowRange {
  id: WindowPresetId;
  label: string;
}

/** The preset the page opens on. */
export const DEFAULT_PRESET: WindowPresetId = 'LAST_3_MONTHS';

/**
 * ⭐ Every date here is a UTC calendar date, not the browser's local one.
 *
 * The server anchors the not-followed-up cut on its OWN date (`LocalDate.now(UTC)`)
 * while taking the window from these strings. Reading the anchor locally makes the two
 * clocks disagree: in UTC+8 between 00:00 and 08:00 local, `getDate()` is already
 * tomorrow, so the page asks for a window ending on a day the server has not reached
 * (measured: the instant 2026-09-17T16:30Z reads as 2026-09-18 locally). The same
 * mismatch shifts the "has this quarter begun" gate — a local `new Date(2026, 9, 1)` is
 * 2026-09-30T16:00Z in UTC+8, so Q4 would appear in the dropdown eight hours early.
 *
 * Using UTC throughout also makes this file's tests independent of the machine's
 * timezone, which matters because CI and this laptop do not share one.
 */
const iso = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/** UTC-date arithmetic; `Date.UTC(y, m, 0)` is the last day of month `m - 1`. */
const firstOf = (y: number, m0: number): Date => new Date(Date.UTC(y, m0, 1));
const lastOf = (y: number, m0: number): Date => new Date(Date.UTC(y, m0 + 1, 0));

const QUARTERS: Record<'Q1' | 'Q2' | 'Q3' | 'Q4', [number, number]> = {
  Q1: [0, 2],
  Q2: [3, 5],
  Q3: [6, 8],
  Q4: [9, 11],
};

/**
 * The window for one preset, or null when the preset has not started yet (asking
 * for Q4 in September). Callers should render only what {@link availablePresets}
 * returns; this returns null rather than throwing so a stale URL cannot break the
 * page.
 */
export function rangeFor(id: WindowPresetId, today: Date): WindowRange | null {
  const y = today.getUTCFullYear();
  const m0 = today.getUTCMonth();

  switch (id) {
    case 'LAST_MONTH':
      // Both bounds computed straight from (y, m0 - 1) rather than by reading a month
      // back off the start Date. `Date.UTC` normalises the negative month, so January's
      // "last month" resolves to the previous December on both bounds without a
      // round-trip through Date getters — and a round-trip is what makes the local/UTC
      // question arise at all. It went wrong here once: reading `start.getMonth()` off a
      // UTC-midnight Date lands in the previous month anywhere west of UTC, which
      // produced startDate > endDate and a 400 from the server.
      return { startDate: iso(firstOf(y, m0 - 1)), endDate: iso(lastOf(y, m0 - 1)) };
    case 'THIS_MONTH':
      return { startDate: iso(firstOf(y, m0)), endDate: iso(today) };
    case 'LAST_3_MONTHS':
      return { startDate: iso(firstOf(y, m0 - 2)), endDate: iso(today) };
    case 'YTD':
      return { startDate: iso(firstOf(y, 0)), endDate: iso(today) };
    default: {
      const [qStart, qEnd] = QUARTERS[id];
      const start = firstOf(y, qStart);
      if (start > today) return null; // quarter has not begun
      const end = lastOf(y, qEnd);
      // In-progress quarter ends today; a finished one ends on its own last day.
      return { startDate: iso(start), endDate: iso(end > today ? today : end) };
    }
  }
}

const LABELS: Record<WindowPresetId, string> = {
  LAST_MONTH: '上个月',
  THIS_MONTH: '本月',
  LAST_3_MONTHS: '近三月',
  Q1: 'Q1',
  Q2: 'Q2',
  Q3: 'Q3',
  Q4: 'Q4',
  YTD: 'YTD',
};

const ORDER: WindowPresetId[] = [
  'LAST_MONTH',
  'THIS_MONTH',
  'LAST_3_MONTHS',
  'Q1',
  'Q2',
  'Q3',
  'Q4',
  'YTD',
];

/**
 * The preset that will ACTUALLY be used for a given clock, together with its range.
 *
 * A selection can stop resolving: quarters only become more available as the clock
 * advances, except across a year boundary — Q4 picked in December 2026 resolves to
 * null once the clock reads January 2027, because Q4 2027 has not started.
 *
 * Returning the id and the range together is the point. Deriving the fallback twice —
 * once for the request, once for the dropdown's label — lets the two disagree, and
 * the page then shows one window's name over another window's numbers.
 */
export function effectiveWindow(
  id: WindowPresetId,
  today: Date,
): { id: WindowPresetId; range: WindowRange } {
  const range = rangeFor(id, today);
  if (range) return { id, range };
  // DEFAULT_PRESET is relative to the clock, so it always resolves.
  return { id: DEFAULT_PRESET, range: rangeFor(DEFAULT_PRESET, today)! };
}

/** Every preset that has started, in dropdown order. */
export function availablePresets(today: Date): WindowPreset[] {
  const out: WindowPreset[] = [];
  for (const id of ORDER) {
    const r = rangeFor(id, today);
    if (r) out.push({ id, label: LABELS[id], ...r });
  }
  return out;
}
